import firebase from 'firebase/app';
import 'firebase/firestore';
import 'firebase/functions';
import 'firebase/auth';
import 'firebase/analytics';
import { cloudFunctions } from '../api/cloudFunctions';
import { firebaseConfig } from './firebase.config';

export const createUserProfileDocument = async (userAuth, additionalData) => {
  if(!userAuth) return;

  const userRef = firestore.doc(`users/${userAuth.uid}`);

  const snapShot = await userRef.get();

  if (!snapShot.exists) {
    const { displayName, email } = userAuth;
    const createdAt = new Date();

    try {
      await userRef.set({
        displayName,
        email,
        createdAt,
        ...additionalData
      })
    } catch (error) {
      console.error('error creating user', error.message);
    }
  }

  return userRef;
};

export const updateUserAttributes = async (currentUser, newAttributes) => {
  if(!currentUser) return;

  const userRef = firestore.collection('users').doc(currentUser.id);

  const snapShot = await userRef.get();

  if (snapShot.exists) {
    try {
      await userRef.update({
        ...newAttributes
      })
    } catch (error) {
      console.error('Error updating user attributes', error.message);
      return error;
    }
  } else {
    throw new Error("User does not exist");
  }

  return userRef;
};

export const updateUserOnboardingStep = async (currentUser, increment) => {
  if(!currentUser) return new Error("User is not signed in");

  const onboardingRef = firestore.collection('onboarding').doc(currentUser.id);

  let stepIncrement = increment || 1;

  try {
    await onboardingRef.set({
      onboardingStepNum: firebase.firestore.FieldValue.increment(stepIncrement)
    }, { merge: true });
  } catch (error) {
    console.error('Error updating step: ', error.message);
    return error;
  }

  return onboardingRef;
};

export const getUserOnboardingDocument = async (currentUser) => {
  if(!currentUser) return new Error("User is not signed in");
  const onboardingRef = firestore.collection('onboarding').doc(currentUser.id);
  try {
    const snapShot = await onboardingRef.get();
    if (snapShot.exists) {
      return snapShot.data();
    } else {
      // No document exists
      return null;
    }
  } catch (error) {
    console.error('Could not retrieve onboarding data: ', error.message);
    return error;
  }
};

export const updateUserOnboardingAttributes = async (currentUser, newAttributes) => {
  if(!currentUser) return new Error("User is not signed in");

  const onboardingRef = firestore.collection('onboarding').doc(currentUser.id);

  try {
    await onboardingRef.set({
      ...newAttributes
    }, { merge: true });
  } catch (error) {
    console.error('Error updating onboarding category values: ', error.message);
    return error;
  }

  return onboardingRef;
};

export const updateOnboardingCategoryValues = async (currentUser, categoryValues) => {
  if(!currentUser) return new Error("User is not signed in");;

  const onboardingRef = firestore.collection('onboarding').doc(currentUser.id);

  try {
    await onboardingRef.set({
      categoryValues
    }, { merge: true });
  } catch (error) {
    console.error('Error updating onboarding category values: ', error.message);
    return error;
  }

  return onboardingRef;
};

export const setUserGoal = async (currentUser, goalId, goalToSet) => {
  if(!currentUser) return new Error("User is not signed in");
  const isNewGoal = !goalId;

  try {
    // Generate document metadata and ref based on new document or not
    const lastModified = new Date();
    let additionalData = { lastModified }
    if (isNewGoal) {
      const createdAt = new Date();
      additionalData = {
        ...additionalData,
        createdAt
      };

      const goalsRef = firestore
        .collection('userGoals').doc(currentUser.id)
        .collection('goals')

      const newDocRef = await goalsRef.add({
        ...goalToSet,
        ...additionalData
      })
      .then(newDoc => {
        return newDoc;
      });

      return newDocRef;

    } else {
      const goalDocRef = firestore
        .collection('userGoals').doc(currentUser.id)
        .collection('goals').doc(goalId);
      await goalDocRef.set({
        ...goalToSet,
        ...additionalData
      }, { merge: true });
      return goalDocRef;
    }
  } catch (error) {
    console.error('Error updating goal data: ', error.message);
    return error;
  }
};

export const createInitialCompanyDocument = async (currentUser, companyAttributes) => {

  try { 
    const companyRef = firestore.collection('companies');
    const createdAt = new Date();
    const newCompanyId = await companyRef.add({
      admins: [ currentUser.id ],
      createdAt,
      ...companyAttributes,
      shifts: [],
      employees: []
    })
    .then(newDoc => {
      return newDoc.id;
    });
    await updateUserAttributes(currentUser, { linkedCompany: newCompanyId, onboardingCompleted: true });
    return newCompanyId;
  } catch (error) {
    console.error('Error creating initial company document: ', error.message);
    return error;
  }
};

export const getCompanyDocument = async (companyId) => {
  const companyRef = firestore.collection('companies').doc(companyId);
  try {
    const snapShot = await companyRef.get();
    if (snapShot.exists) {
      return snapShot.data();
    } else {
      // No document exists
      return null;
    }
  } catch (error) {
    console.error('Could not retrieve company data: ', error.message);
    return error;
  }
};

export const addEmployeeDocument = async (currentCompany, newEmployeeData) => {
  try {

    // First create the new employee
    const employeesRef = firestore.collection('employees');
    const createdAt = new Date();
    const newEmployeeId = await employeesRef.add({
      createdAt,
      ...newEmployeeData
    })
    .then(newDoc => {
      return newDoc.id;
    });

    // Now add this employee to the company employee list
    const companyRef = firestore.collection('companies').doc(currentCompany.companyId);
    await companyRef.update({
      employees: firebase.firestore.FieldValue.arrayUnion(newEmployeeId)
    });

    // Finally, update the linked shift assignedEmployees list
    if (newEmployeeData.shiftAssignment !== "Unassigned") {
      const shiftRef = firestore.collection('shifts').doc(newEmployeeData.shiftAssignment);
      await shiftRef.update({
        assignedEmployees: firebase.firestore.FieldValue.arrayUnion(newEmployeeId)
      });
    }

    // Return the new employee id
    return newEmployeeId;
  } catch (error) {
    console.error('Error creating new employee: ', error.message);
    return error;
  }
};

export const updateEmployeeDocument = async (employeeId, employeeData) => {
  try {

    // First, update the linked shift assignedEmployees
    if (employeeData.shiftAssignment !== "Unassigned") {
      const shiftRef = firestore.collection('shifts').doc(employeeData.shiftAssignment);
      await shiftRef.update({
        assignedEmployees: firebase.firestore.FieldValue.arrayUnion(employeeId)
      });
    }

    // Finally, update the employee document, including last modified date
    const employeeRef = firestore.collection('employees').doc(employeeId);
    const lastModified = new Date();
    const updateObject = {
      lastModified,
      ...employeeData
    };
    await employeeRef.update(updateObject);
    return updateObject;
  } catch (error) {
    console.error('Error updating employee: ', error.message);
    return error;
  }
};

export const deleteEmployeeDocument = async (currentCompany, employeeId) => {
  try {

    // First, catch the affected shift and remove the employee from the assignedEmployees list
    const employeeRef = firestore.collection('employees').doc(employeeId);
    const priorEmployeeRef = await employeeRef.get();
    const priorEmployeeData = priorEmployeeRef.data();
    const shiftAssignment = priorEmployeeData.shiftAssignment;
    if (shiftAssignment && shiftAssignment !== "Unassigned") {
      const shiftRef = firestore.collection('shifts').doc(shiftAssignment);
      await shiftRef.update({
        assignedEmployees: firebase.firestore.FieldValue.arrayRemove(employeeId)
      });
    }

    // Now delete this employee from the company employee list
    const companyRef = firestore.collection('companies').doc(currentCompany.companyId);
    await companyRef.update({
      employees: firebase.firestore.FieldValue.arrayRemove(employeeId)
    });

    // Finally, delete the new employee from the employees collection
    await employeeRef.delete();

    // Return the new employee id
    return employeeId;
  } catch (error) {
    console.error('Error deleting employee: ', error.message);
    return error;
  }
};

export const getCurrentEmployeeDocuments = async (currentCompany) => {
  const employeesToQuery = currentCompany.employees;
  if (!employeesToQuery) return null;
  try {
    const employeeRefs = employeesToQuery.map(employeeId => firestore.collection('employees').doc(employeeId).get());
    return Promise.all(employeeRefs)
    .then(employeeDocs => {
      let employeeData = {};
      employeeDocs.forEach((doc) => {
        employeeData[doc.id] = doc.data();
      });
      return employeeData;
    });
  } catch (error) {
    console.error('Could not retrieve employee data: ', error.message);
    return error;
  }
};

/////// Shifts

export const addShiftDocument = async (currentCompany, newShiftData) => {
  try {

    // First create the new shift
    const shiftsRef = firestore.collection('shifts');
    const createdAt = new Date();
    const newShiftId = await shiftsRef.add({
      createdAt,
      ...newShiftData
    })
    .then(newDoc => {
      return newDoc.id;
    });

    // Now add this shift to the company shift list
    const companyRef = firestore.collection('companies').doc(currentCompany.companyId);
    await companyRef.update({
      shifts: firebase.firestore.FieldValue.arrayUnion(newShiftId)
    });

    // Finally, update each employee shift assignment, if any
    if (newShiftData.assignedEmployees.length) {
      const updateEmployeeShiftRefs = newShiftData.assignedEmployees.map(employeeId => updateEmployeeShiftAssignment(employeeId, newShiftId));
      await Promise.all(updateEmployeeShiftRefs);
    }

    // Return the new shift id
    return newShiftId;
  } catch (error) {
    console.error('Error creating new shift: ', error.message);
    return error;
  }
};

export const updateShiftDocument = async (shiftId, shiftData) => {
  try {
    
    // First, catch any employees who have been removed from the shift and update them to Unassigned
    const shiftRef = firestore.collection('shifts').doc(shiftId);
    const priorShiftRef = await shiftRef.get();
    const priorShiftData = priorShiftRef.data();
    const droppedEmployees = priorShiftData.assignedEmployees.filter(id => !shiftData.assignedEmployees.includes(id));
    if (droppedEmployees.length) {
      const updateEmployeeShiftRefs = droppedEmployees.map(employeeId => updateEmployeeShiftAssignment(employeeId, "Unassigned"));
      await Promise.all(updateEmployeeShiftRefs);
    }

    // Next, catch any newly assigned employees to this shift, if any
    const newEmployees = shiftData.assignedEmployees.filter(id => !priorShiftData.assignedEmployees.includes(id));
    if (newEmployees.length) {
      const updateEmployeeShiftRefs = newEmployees.map(employeeId => updateEmployeeShiftAssignment(employeeId, shiftId));
      await Promise.all(updateEmployeeShiftRefs);
    }

    // Finally, update the shift document, including last modified date
    const lastModified = new Date();
    const updateObject = {
      lastModified,
      ...shiftData
    };
    await shiftRef.update(updateObject);

    return updateObject;
  } catch (error) {
    console.error('Error updating shift: ', error.message);
    return error;
  }
};

export const deleteShiftDocument = async (currentCompany, shiftId) => {
  try {

    // First, catch any employees who have been removed from the old shift and update them to Unassigned
    const shiftRef = firestore.collection('shifts').doc(shiftId);
    const priorShiftRef = await shiftRef.get();
    const priorShiftData = priorShiftRef.data();
    const droppedEmployees = priorShiftData.assignedEmployees;
    if (droppedEmployees.length) {
      const updateEmployeeShiftRefs = droppedEmployees.map(employeeId => updateEmployeeShiftAssignment(employeeId, "Unassigned"));
      await Promise.all(updateEmployeeShiftRefs);
    }

    // Next, delete this shift from the company shift list
    const companyRef = firestore.collection('companies').doc(currentCompany.companyId);
    await companyRef.update({
      shifts: firebase.firestore.FieldValue.arrayRemove(shiftId)
    });

    // Finally, delete the new shift from the shifts collection
    await shiftRef.delete();

    // Return the new shift id
    return shiftId;
  } catch (error) {
    console.error('Error deleting shift: ', error.message);
    return error;
  }
};

export const getCurrentShiftDocuments = async (currentCompany) => {
  const shiftsToQuery = currentCompany.shifts;
  if (!shiftsToQuery) return null;
  try {
    const shiftRefs = shiftsToQuery.map(shiftId => firestore.collection('shifts').doc(shiftId).get());
    return Promise.all(shiftRefs)
    .then(shiftDocs => {
      let shiftData = {};
      shiftDocs.forEach((doc) => {
        shiftData[doc.id] = doc.data();
      });
      return shiftData;
    });
  } catch (error) {
    console.error('Could not retrieve shift data: ', error.message);
    return error;
  }
};

export const updateEmployeeShiftAssignment = async (employeeId, shiftId) => {
  try {
    // Update the employee document, including last modified date
    const employeeRef = firestore.collection('employees').doc(employeeId);
    const lastModified = new Date();
    const updateObject = {
      lastModified,
      shiftAssignment: shiftId || "Unassigned"
    };
    await employeeRef.update(updateObject);
    return updateObject;
  } catch (error) {
    console.error('Error updating employee shift assignment: ', error.message);
    return error;
  }
};

// Accounts 

export const addAccountDocument = async (publicToken, accounts, institution, linkSessionId) => {
  try {
    const result = await functions.httpsCallable("createAccountDocument")({
      publicToken, accounts, institution, linkSessionId
    });
    return result.data;
  } catch (error) {
    const eMsg = 'Error creating new account link: ' + error.message;
    console.error(eMsg);
    throw new Error(eMsg);
  }
};

export const getCurrentAccountDocuments = async () => {
  try {
    const result = await functions.httpsCallable("getAccountDocuments")();
    return result.data;
  } catch (error) {
    const eMsg = 'Error retrieving linked accounts: ' + error.message;
    console.error(eMsg);
    throw new Error(eMsg);
  }
};

export const updateAccountDocument = async (shiftId, shiftData) => {
  try {
    
    // First, catch any employees who have been removed from the shift and update them to Unassigned
    const shiftRef = firestore.collection('shifts').doc(shiftId);
    const priorShiftRef = await shiftRef.get();
    const priorShiftData = priorShiftRef.data();
    const droppedEmployees = priorShiftData.assignedEmployees.filter(id => !shiftData.assignedEmployees.includes(id));
    if (droppedEmployees.length) {
      const updateEmployeeShiftRefs = droppedEmployees.map(employeeId => updateEmployeeShiftAssignment(employeeId, "Unassigned"));
      await Promise.all(updateEmployeeShiftRefs);
    }

    // Next, catch any newly assigned employees to this shift, if any
    const newEmployees = shiftData.assignedEmployees.filter(id => !priorShiftData.assignedEmployees.includes(id));
    if (newEmployees.length) {
      const updateEmployeeShiftRefs = newEmployees.map(employeeId => updateEmployeeShiftAssignment(employeeId, shiftId));
      await Promise.all(updateEmployeeShiftRefs);
    }

    // Finally, update the shift document, including last modified date
    const lastModified = new Date();
    const updateObject = {
      lastModified,
      ...shiftData
    };
    await shiftRef.update(updateObject);

    return updateObject;
  } catch (error) {
    console.error('Error updating shift: ', error.message);
    return error;
  }
};

export const deleteAccountDocument = async (accountId) => {
  try {
    const result = await functions.httpsCallable("deleteAccountDocument")({
      accountId
    });
    return result.data;
  } catch (error) {
    const eMsg = 'Error removing account link: ' + error.message;
    console.error(eMsg);
    throw new Error(eMsg);
  }
};

export const updateEmployeeAccountAssignment = async (employeeId, shiftId) => {
  try {
    // Update the employee document, including last modified date
    const employeeRef = firestore.collection('employees').doc(employeeId);
    const lastModified = new Date();
    const updateObject = {
      lastModified,
      shiftAssignment: shiftId || "Unassigned"
    };
    await employeeRef.update(updateObject);
    return updateObject;
  } catch (error) {
    console.error('Error updating employee shift assignment: ', error.message);
    return error;
  }
};

// Account balances

export const pollHistoricalAccountBalances = async () => {
  try {
    // TBD check access code securely on DB, also pause user and exchange a token
    // For now, let's do this quick and easy because it's not a big deal
    const res = await cloudFunctions.post('pollHistoricalAccountBalance', {});
    return res.data;
  } catch (error) {
    console.error('Error updating historical account balances: ', error.message);
    return error;
  }
};

export const getRecentAccountBalances = async () => {
  try {
    const result = await functions.httpsCallable("getRecentAccountBalances")();
    return result.data;
  } catch (error) {
    const eMsg = 'Error retrieving recent account balances: ' + error.message;
    console.error(eMsg);
    throw new Error(eMsg);
  }
};

export const getHistoricalAccountBalances = async () => {
  try {
    const result = await functions.httpsCallable("getHistoricalAccountBalances")();
    return result.data;
  } catch (error) {
    const eMsg = 'Error retrieving historical account balances: ' + error.message;
    console.error(eMsg);
    throw new Error(eMsg);
  }
};

// Transactions

export const addTransactionDocument = async (publicToken, accounts, institution, linkSessionId) => {
  try {
    const result = await functions.httpsCallable("createAccountDocument")({
      publicToken, accounts, institution, linkSessionId
    });
    return result.data;
  } catch (error) {
    const eMsg = 'Error creating new account link: ' + error.message;
    console.error(eMsg);
    throw new Error(eMsg);
  }
};

export const getCurrentTransactionDocuments = async () => {
  try {
    const result = await functions.httpsCallable("getTransactionDocuments")();
    return result.data;
  } catch (error) {
    const eMsg = 'Error retrieving transactions: ' + error.message;
    console.error(eMsg);
    throw new Error(eMsg);
  }
};

export const getSelectedTransactionDocuments = async (selectedDate) => {
  try {
    const result = await functions.httpsCallable("getSelectedTransactionDocuments")({ selectedDate });
    return result.data;
  } catch (error) {
    const eMsg = 'Error retrieving transactions: ' + error.message;
    console.error(eMsg);
    throw new Error(eMsg);
  }
};

export const updateTransactionDocument = async (shiftId, shiftData) => {
  try {
    
    // First, catch any employees who have been removed from the shift and update them to Unassigned
    const shiftRef = firestore.collection('shifts').doc(shiftId);
    const priorShiftRef = await shiftRef.get();
    const priorShiftData = priorShiftRef.data();
    const droppedEmployees = priorShiftData.assignedEmployees.filter(id => !shiftData.assignedEmployees.includes(id));
    if (droppedEmployees.length) {
      const updateEmployeeShiftRefs = droppedEmployees.map(employeeId => updateEmployeeShiftAssignment(employeeId, "Unassigned"));
      await Promise.all(updateEmployeeShiftRefs);
    }

    // Next, catch any newly assigned employees to this shift, if any
    const newEmployees = shiftData.assignedEmployees.filter(id => !priorShiftData.assignedEmployees.includes(id));
    if (newEmployees.length) {
      const updateEmployeeShiftRefs = newEmployees.map(employeeId => updateEmployeeShiftAssignment(employeeId, shiftId));
      await Promise.all(updateEmployeeShiftRefs);
    }

    // Finally, update the shift document, including last modified date
    const lastModified = new Date();
    const updateObject = {
      lastModified,
      ...shiftData
    };
    await shiftRef.update(updateObject);

    return updateObject;
  } catch (error) {
    console.error('Error updating shift: ', error.message);
    return error;
  }
};

export const deleteTransactionDocument = async (accountId) => {
  try {
    const result = await functions.httpsCallable("deleteAccountDocument")({
      accountId
    });
    return result.data;
  } catch (error) {
    const eMsg = 'Error removing account link: ' + error.message;
    console.error(eMsg);
    throw new Error(eMsg);
  }
};

export const updateEmployeeTransactionAssignment = async (employeeId, shiftId) => {
  try {
    // Update the employee document, including last modified date
    const employeeRef = firestore.collection('employees').doc(employeeId);
    const lastModified = new Date();
    const updateObject = {
      lastModified,
      shiftAssignment: shiftId || "Unassigned"
    };
    await employeeRef.update(updateObject);
    return updateObject;
  } catch (error) {
    console.error('Error updating employee shift assignment: ', error.message);
    return error;
  }
};

// Models

export const addModelDocument = async (publicToken, accounts, institution, linkSessionId) => {
  try {
    const result = await functions.httpsCallable("createAccountDocument")({
      publicToken, accounts, institution, linkSessionId
    });
    return result.data;
  } catch (error) {
    const eMsg = 'Error creating new account link: ' + error.message;
    console.error(eMsg);
    throw new Error(eMsg);
  }
};

export const getCurrentModelDocuments = async () => {
  try {
    const result = await functions.httpsCallable("getModelDocuments")();
    return result.data;
  } catch (error) {
    const eMsg = 'Could not retrieve financial models';
    console.error(eMsg);
    throw new Error(eMsg);
  }
};

export const getSelectedModelDocuments = async (selectedDate) => {
  try {
    const result = await functions.httpsCallable("getSelectedTransactionDocuments")({ selectedDate });
    return result.data;
  } catch (error) {
    const eMsg = 'Error retrieving transactions: ' + error.message;
    console.error(eMsg);
    throw new Error(eMsg);
  }
};

export const updateModelDocument = async (shiftId, shiftData) => {
  try {
    
    // First, catch any employees who have been removed from the shift and update them to Unassigned
    const shiftRef = firestore.collection('shifts').doc(shiftId);
    const priorShiftRef = await shiftRef.get();
    const priorShiftData = priorShiftRef.data();
    const droppedEmployees = priorShiftData.assignedEmployees.filter(id => !shiftData.assignedEmployees.includes(id));
    if (droppedEmployees.length) {
      const updateEmployeeShiftRefs = droppedEmployees.map(employeeId => updateEmployeeShiftAssignment(employeeId, "Unassigned"));
      await Promise.all(updateEmployeeShiftRefs);
    }

    // Next, catch any newly assigned employees to this shift, if any
    const newEmployees = shiftData.assignedEmployees.filter(id => !priorShiftData.assignedEmployees.includes(id));
    if (newEmployees.length) {
      const updateEmployeeShiftRefs = newEmployees.map(employeeId => updateEmployeeShiftAssignment(employeeId, shiftId));
      await Promise.all(updateEmployeeShiftRefs);
    }

    // Finally, update the shift document, including last modified date
    const lastModified = new Date();
    const updateObject = {
      lastModified,
      ...shiftData
    };
    await shiftRef.update(updateObject);

    return updateObject;
  } catch (error) {
    console.error('Error updating shift: ', error.message);
    return error;
  }
};

export const deleteModelDocument = async (accountId) => {
  try {
    const result = await functions.httpsCallable("deleteAccountDocument")({
      accountId
    });
    return result.data;
  } catch (error) {
    const eMsg = 'Error removing account link: ' + error.message;
    console.error(eMsg);
    throw new Error(eMsg);
  }
};

export const updateEmployeeModelAssignment = async (employeeId, shiftId) => {
  try {
    // Update the employee document, including last modified date
    const employeeRef = firestore.collection('employees').doc(employeeId);
    const lastModified = new Date();
    const updateObject = {
      lastModified,
      shiftAssignment: shiftId || "Unassigned"
    };
    await employeeRef.update(updateObject);
    return updateObject;
  } catch (error) {
    console.error('Error updating employee shift assignment: ', error.message);
    return error;
  }
};

// export const addCollectionAndDocuments = async (collectionKey, objectsToAdd) => {
//   const collectionRef = firestore.collection(collectionKey);
//   console.log(collectionRef);

//   console.log("objectsToAdd: ",objectsToAdd);

//   const batch = firestore.batch();
//   objectsToAdd.forEach(obj => {
//     const newDocRef = collectionRef.doc(); // generates a new ID randomly
//     batch.set(newDocRef, obj)
//   });

//   return await batch.commit();
// };

// export const convertCollectionsSnapshotToMap = (collections) => {
//   const transformedCollection = collections.docs.map(doc => {
//     const { title, items } = doc.data();

//     return {
//       routeName: encodeURI(title.toLowerCase()),
//       id: doc.id,
//       title,
//       items
//     }
//   });

//   return transformedCollection.reduce((accumulator, collection) => {
//     accumulator[collection.title.toLowerCase()] = collection;
//     return accumulator;
//   }, {});
// };

export const getCurrentUser = () => {
  return new Promise((resolve, reject) => {
    const unsubscribe = auth.onAuthStateChanged(userAuth => {
      unsubscribe();
      resolve(userAuth);
    }, reject);
  })
}

firebase.initializeApp(firebaseConfig);

// Init google analytics
firebase.analytics();

export const auth = firebase.auth();
export const firestore = firebase.firestore();
export const functions = firebase.functions();

export const googleProvider = new firebase.auth.GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: 'select_account' });
export const signInWithGoogle = () => auth.signInWithPopup(googleProvider);

export default firebase;